import { createTransformedImports, isBabelApi } from './utils.js';
/**
 * Wraps the thunk argument of `useReactive` / `useReactiveDeep` in
 * `React.useCallback(fn, [deps])`. The captured identifiers are collected from
 * the thunk body the same way the `callback` transform does for `reactive()`
 * callbacks, giving the hook a stable identity across renders when captures are
 * equal. This lets the runtime reuse the underlying `ReactiveSignal`.
 *
 * Runs after the async and callback transforms so the inner function has
 * already been rewritten (e.g. `async` → `function*`).
 */
function createSignaliumUseReactiveTransform(api, opts) {
    // Only forward user-provided `transformedImports` entries that target the
    // hook names we care about. The shared `transformedImports` preset option is
    // also used by the callback/async transforms to retarget arbitrary identifiers
    // like `reactive`/`task`/`relay`; we must not accidentally pick those up here
    // or we'd wrap inner arrows with `useCallback` in non-React code paths.
    const trackedNames = new Set(['useReactive', 'useReactiveShallow', 'useReactiveDeep']);
    const filteredAdditional = opts?.transformedImports?.filter(([name]) => trackedNames.has(name));
    const transformedImports = createTransformedImports([
        ['useReactive', ['signalium/react']],
        ['useReactiveShallow', ['signalium/react']],
        ['useReactiveDeep', ['signalium/react']],
    ], filteredAdditional, opts?.importPaths);
    const t = api.types;
    const reactImportPath = opts?.reactImportPath ?? 'react';
    const isTrackedImport = (localName, path) => {
        const binding = path.scope.getBinding(localName);
        if (!binding || !t.isImportSpecifier(binding.path.node))
            return false;
        const importSpec = binding.path.node;
        const importedName = importSpec.imported.name;
        const importDecl = binding.path.parent;
        if (!t.isImportDeclaration(importDecl))
            return false;
        const importPaths = transformedImports.get(importedName);
        if (!importPaths)
            return false;
        return importPaths.some(p => typeof p === 'string' ? importDecl.source.value === p : p.test(importDecl.source.value));
    };
    const isTargetCall = (path) => {
        const callee = path.node.callee;
        if (!t.isIdentifier(callee))
            return false;
        return isTrackedImport(callee.name, path);
    };
    function isIdentifierInTypePosition(refPath) {
        let current = refPath.parentPath;
        let child = refPath;
        while (current) {
            const nodeType = current.node.type;
            if (nodeType && nodeType.startsWith('TS')) {
                if (current.isTSAsExpression() ||
                    current.isTSSatisfiesExpression?.() ||
                    current.isTSNonNullExpression() ||
                    current.isTSInstantiationExpression?.()) {
                    if (child.key === 'expression')
                        return false;
                    return true;
                }
                return true;
            }
            child = current;
            current = current.parentPath;
        }
        return false;
    }
    function ensureUseCallbackIdentifier(programPath) {
        // Find an existing `import { useCallback as X } from 'react'`
        for (const bodyPath of programPath.get('body')) {
            if (!bodyPath.isImportDeclaration())
                continue;
            const importDecl = bodyPath.node;
            if (importDecl.source.value !== reactImportPath)
                continue;
            for (const spec of importDecl.specifiers) {
                if (spec.type !== 'ImportSpecifier')
                    continue;
                const ispec = spec;
                const imported = ispec.imported;
                if (imported && imported.name === 'useCallback') {
                    return ispec.local.name;
                }
            }
        }
        // Augment an existing `import ... from 'react'`
        for (const bodyPath of programPath.get('body')) {
            if (!bodyPath.isImportDeclaration())
                continue;
            const node = bodyPath.node;
            if (node.source.value !== reactImportPath)
                continue;
            const localName = programPath.scope.generateUidIdentifier('useCallback').name;
            node.specifiers.push(t.importSpecifier(t.identifier(localName), t.identifier('useCallback')));
            return localName;
        }
        // Otherwise, insert a new import
        const localName = programPath.scope.generateUidIdentifier('useCallback').name;
        const importDecl = t.importDeclaration([t.importSpecifier(t.identifier(localName), t.identifier('useCallback'))], t.stringLiteral(reactImportPath));
        const [first] = programPath.get('body');
        if (first) {
            first.insertBefore(importDecl);
        }
        else {
            programPath.pushContainer('body', importDecl);
        }
        return localName;
    }
    function collectDeps(innerFn) {
        const depNames = new Set();
        const innerScope = innerFn.scope;
        const innerNode = innerFn.node;
        innerFn.traverse({
            ReferencedIdentifier(refPath) {
                const nearestFn = refPath.getFunctionParent();
                if (!nearestFn || nearestFn.node !== innerNode)
                    return;
                const name = refPath.node.name;
                const binding = refPath.scope.getBinding(name);
                if (!binding)
                    return;
                if (isIdentifierInTypePosition(refPath))
                    return;
                if (binding.scope.path.isProgram())
                    return;
                let declScope = binding.scope;
                while (declScope) {
                    if (declScope === innerScope)
                        return;
                    declScope = declScope.parent;
                }
                if (binding.kind === 'param' && binding.scope === innerScope)
                    return;
                depNames.add(name);
            },
        });
        return Array.from(depNames);
    }
    function isAlreadyMemoized(argPath) {
        if (!argPath.isCallExpression())
            return false;
        const callee = argPath.node.callee;
        if (!t.isIdentifier(callee))
            return false;
        const name = callee.name;
        // Heuristic: any identifier ending in `useCallback` or `useMemo` is treated
        // as already-memoized. Avoids double-wrapping and supports aliased imports.
        return name === 'useCallback' || name === 'useMemo' || /useCallback$/.test(name);
    }
    return {
        name: 'signalium-transform-use-reactive',
        visitor: {
            CallExpression(callPath) {
                if (!isTargetCall(callPath))
                    return;
                const args = callPath.get('arguments');
                // Only the thunk form: exactly one argument and it is a function.
                if (args.length !== 1)
                    return;
                let fnPath = args[0];
                // Unwrap TS expression wrappers (`as`, `satisfies`, `!`, etc.) so the
                // user can still write `useReactive((async () => ...) as X)`.
                while (fnPath.isTSAsExpression() ||
                    fnPath.isTSSatisfiesExpression?.() ||
                    fnPath.isTSNonNullExpression() ||
                    fnPath.isTSInstantiationExpression?.()) {
                    fnPath = fnPath.get('expression');
                }
                if (!(fnPath.isArrowFunctionExpression() || fnPath.isFunctionExpression()))
                    return;
                if (isAlreadyMemoized(fnPath))
                    return;
                const programPath = callPath.findParent((p) => p.isProgram());
                const useCallbackName = ensureUseCallbackIdentifier(programPath);
                const innerFn = fnPath;
                const deps = collectDeps(innerFn);
                const wrapped = t.callExpression(t.identifier(useCallbackName), [
                    innerFn.node,
                    t.arrayExpression(deps.map(n => t.identifier(n))),
                ]);
                fnPath.replaceWith(wrapped);
                fnPath.skip();
            },
        },
    };
}
export function signaliumUseReactiveTransform(apiOrOpts, maybeOpts) {
    if (isBabelApi(apiOrOpts)) {
        return createSignaliumUseReactiveTransform(apiOrOpts, maybeOpts);
    }
    return (api) => createSignaliumUseReactiveTransform(api, apiOrOpts);
}
